/**
 * Utilidad para cálculos de métricas y estadísticas de órdenes de producción
 */
class MetricasCalculadora {
  /**
   * Calcula el tiempo total de un array de procesos
   * @param {Array} procesos - Array de procesos con propiedad tiempo_proceso
   * @returns {number} Tiempo total en horas
   */
  static calcularTiempoTotal(procesos) {
    if (!Array.isArray(procesos)) return 0;
    
    return procesos.reduce((total, proceso) => {
      const tiempo = proceso?.tiempo_proceso;
      return total + (typeof tiempo === 'number' && tiempo > 0 ? tiempo : 0);
    }, 0);
  }

  /**
   * Calcula el porcentaje de tiempo que representa un proceso del total
   * @param {number} tiempoProceso - Tiempo del proceso en horas
   * @param {number} tiempoTotal - Tiempo total en horas
   * @returns {number} Porcentaje (0-100)
   */
  static calcularPorcentajeTiempo(tiempoProceso, tiempoTotal) {
    if (typeof tiempoProceso !== 'number' || typeof tiempoTotal !== 'number') return 0;
    if (tiempoTotal <= 0) return 0;
    
    const porcentaje = (tiempoProceso / tiempoTotal) * 100;
    return Math.round(porcentaje * 100) / 100; // Redondear a 2 decimales
  }

  /**
   * Calcula la eficiencia promedio basada en procesos completados
   * @param {Array} procesos - Array de procesos con propiedad estado
   * @returns {number} Eficiencia promedio (0-100)
   */
  static calcularEficienciaPromedio(procesos) {
    if (!Array.isArray(procesos) || procesos.length === 0) return 0;
    
    const procesosCompletados = procesos.filter(proceso => 
      proceso?.estado === 'Completado' || proceso?.estado === 'Finalizado'
    );
    
    const eficiencia = (procesosCompletados.length / procesos.length) * 100;
    return Math.round(eficiencia * 100) / 100; // Redondear a 2 decimales
  }

  /**
   * Calcula el tiempo entre dos fechas en horas
   * @param {string} fechaInicio - Fecha de inicio (ISO string)
   * @param {string} fechaFin - Fecha de fin (ISO string)
   * @returns {number} Tiempo en horas
   */
  static calcularTiempoEntreFechas(fechaInicio, fechaFin) {
    if (!fechaInicio || !fechaFin) return 0;
    
    try {
      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);
      
      if (isNaN(inicio.getTime()) || isNaN(fin.getTime())) return 0;
      
      const diffMs = fin.getTime() - inicio.getTime();
      if (diffMs < 0) return 0; // No permitir tiempos negativos
      
      return diffMs / (1000 * 60 * 60); // Convertir a horas
    } catch (error) {
      return 0;
    }
  }

  /**
   * Formatea el tiempo en un string legible
   * @param {number} horas - Tiempo en horas
   * @returns {string} Tiempo formateado
   */
  static formatearTiempo(horas) {
    if (typeof horas !== 'number' || horas < 0) return 'N/A';
    
    if (horas === 0) return '0 horas';
    if (horas < 1) {
      const minutos = Math.round(horas * 60);
      return `${minutos} min`;
    } else if (horas === 1) {
      return '1 hora';
    } else {
      // Redondear a 2 decimales para horas
      const horasRedondeadas = Math.round(horas * 100) / 100;
      return `${horasRedondeadas} horas`;
    }
  }

  /**
   * Formatea el porcentaje en un string legible
   * @param {number} porcentaje - Porcentaje (0-100)
   * @returns {string} Porcentaje formateado
   */
  static formatearPorcentaje(porcentaje) {
    if (typeof porcentaje !== 'number' || porcentaje < 0) return 'N/A';
    
    const redondeado = Math.round(porcentaje * 100) / 100;
    return `${redondeado}%`;
  }

  /**
   * Determina el estado de un proceso basado en las fechas
   * @param {string} fechaInicio - Fecha de inicio
   * @param {string} fechaFin - Fecha de fin
   * @returns {string} Estado del proceso
   */
  static determinarEstadoProceso(fechaInicio, fechaFin) {
    if (!fechaInicio) return 'Pendiente';
    if (!fechaFin) return 'En Proceso';
    return 'Completado';
  }

  /**
   * Calcula el tiempo promedio de los procesos
   * @param {Array} procesos - Array de procesos con propiedad tiempo_proceso
   * @returns {number} Tiempo promedio en horas
   */
  static calcularTiempoPromedioProceso(procesos) {
    if (!Array.isArray(procesos) || procesos.length === 0) return 0;
    
    const tiemposValidos = procesos
      .map(p => p?.tiempo_proceso)
      .filter(tiempo => typeof tiempo === 'number' && tiempo >= 0);
    
    if (tiemposValidos.length === 0) return 0;
    
    const suma = tiemposValidos.reduce((total, tiempo) => total + tiempo, 0);
    return Math.round((suma / tiemposValidos.length) * 100) / 100;
  }

  /**
   * Calcula estadísticas de resumen para una orden
   * @param {Array} procesos - Array de procesos
   * @param {Object} ordenData - Datos de la orden
   * @returns {Object} Estadísticas de resumen
   */
  static calcularEstadisticasResumen(procesos, ordenData) {
    const tiempoTotal = this.calcularTiempoTotal(procesos);
    const procesosCompletados = procesos.filter(p => 
      p?.estado === 'Completado' || p?.estado === 'Finalizado'
    ).length;
    const procesosTotales = procesos.length;
    const eficienciaPromedio = this.calcularEficienciaPromedio(procesos);
    
    // Calcular tiempo de creación (desde created hasta activa.fecha_estado)
    const tiempoCreacion = ordenData?.created && ordenData?.expand?.activa?.fecha_estado
      ? this.calcularTiempoEntreFechas(ordenData.created, ordenData.expand.activa.fecha_estado)
      : 0;
    
    // Calcular tiempo de ejecución
    let tiempoEjecucion = 0;
    let fechaFinalizacionCalculada = null;
    
    if (ordenData?.expand?.activa?.fecha_estado) {
      // Si la orden está finalizada, usar la fecha de finalización
      if (ordenData?.expand?.finalizada?.fecha_estado) {
        tiempoEjecucion = this.calcularTiempoEntreFechas(
          ordenData.expand.activa.fecha_estado, 
          ordenData.expand.finalizada.fecha_estado
        );
        fechaFinalizacionCalculada = ordenData.expand.finalizada.fecha_estado;
      } else {
        // Si no está finalizada, buscar la fecha más reciente de inicio o fin de los procesos
        const fechasProcesos = procesos
          .map(p => [p.fecha_inicio, p.fecha_fin])
          .flat()
          .filter(fecha => fecha && fecha !== null)
          .map(fecha => new Date(fecha))
          .filter(fecha => !isNaN(fecha.getTime()));
        
        if (fechasProcesos.length > 0) {
          const fechaMasReciente = new Date(Math.max(...fechasProcesos.map(d => d.getTime())));
          tiempoEjecucion = this.calcularTiempoEntreFechas(
            ordenData.expand.activa.fecha_estado,
            fechaMasReciente.toISOString()
          );
          fechaFinalizacionCalculada = fechaMasReciente.toISOString();
        }
      }
    }


    return {
      tiempoTotal,
      procesosCompletados,
      procesosTotales,
      eficienciaPromedio,
      tiempoCreacion,
      tiempoEjecucion,
      fechaCreacion: ordenData?.created || null,
      fechaInicio: ordenData?.expand?.activa?.fecha_estado || null,
      fechaFinalizacion: fechaFinalizacionCalculada || ordenData?.expand?.finalizada?.fecha_estado || null
    };
  }

  /**
   * Procesa un array de procesos y calcula métricas adicionales
   * @param {Array} procesos - Array de procesos
   * @returns {Array} Procesos con métricas calculadas
   */
  static procesarProcesos(procesos) {
    if (!Array.isArray(procesos)) return [];
    
    const tiempoTotal = this.calcularTiempoTotal(procesos);
    
    return procesos.map(proceso => {
      const tiempoProceso = proceso?.tiempo_proceso || 0;
      const porcentajeTiempo = this.calcularPorcentajeTiempo(tiempoProceso, tiempoTotal);
      const estado = this.determinarEstadoProceso(proceso?.fecha_inicio, proceso?.fecha_fin);
      
      return {
        ...proceso,
        tiempo_proceso: tiempoProceso,
        porcentaje_tiempo: porcentajeTiempo,
        estado
      };
    });
  }

  /**
   * Calcula métricas de rendimiento para una orden
   * @param {Array} procesos - Array de procesos
   * @param {Object} ordenData - Datos de la orden
   * @returns {Object} Métricas de rendimiento
   */
  static calcularMetricasRendimiento(procesos, ordenData) {
    const resumen = this.calcularEstadisticasResumen(procesos, ordenData);
    const tiempoPromedioProceso = this.calcularTiempoPromedioProceso(procesos);
    
    // Calcular procesos por día
    const procesosPorDia = resumen.tiempoEjecucion > 0 
      ? resumen.procesosTotales / (resumen.tiempoEjecucion / 24)
      : 0;
    
    // Calcular eficiencia de tiempo (tiempo real vs tiempo planificado)
    const eficienciaTiempo = resumen.tiempoTotal > 0 && resumen.tiempoEjecucion > 0
      ? (resumen.tiempoTotal / resumen.tiempoEjecucion) * 100
      : 0;

    return {
      ...resumen,
      tiempoPromedioProceso,
      procesosPorDia: Math.round(procesosPorDia * 100) / 100,
      eficienciaTiempo: Math.round(eficienciaTiempo * 100) / 100
    };
  }

  /**
   * Valida si los datos de entrada son válidos
   * @param {Array} procesos - Array de procesos
   * @param {Object} ordenData - Datos de la orden
   * @returns {Object} Resultado de la validación
   */
  static validarDatos(procesos, ordenData) {
    const errores = [];
    
    if (!Array.isArray(procesos)) {
      errores.push('Los procesos deben ser un array');
    }
    
    if (!ordenData || typeof ordenData !== 'object') {
      errores.push('Los datos de la orden son requeridos');
    }
    
    if (procesos && procesos.length > 0) {
      const procesosInvalidos = procesos.filter(p => 
        !p || typeof p !== 'object' || !p.id
      );
      
      if (procesosInvalidos.length > 0) {
        errores.push(`${procesosInvalidos.length} procesos tienen datos inválidos`);
      }
    }
    
    return {
      esValido: errores.length === 0,
      errores
    };
  }
}

export default MetricasCalculadora;
